/**
 * Find a returning patient, or start a new file.
 *
 * One box, searched by whatever the patient hands over: the four-part name, the
 * national ID, either phone, or the insurance code. The desk does not have to
 * know which it is holding. A debounce keeps a fast typist from sending a
 * request per keystroke.
 */

import { useEffect, useState } from 'react';
import { Badge, Button, EmptyState, Icon, Input, Spinner } from '../../components/common';
import { patientsApi } from '../../api/endpoints';

export function PatientFinder({ onSelect, onCreateNew, selectedId = null, canCreate = true }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await patientsApi.search({ q: q.trim() || undefined, limit: 30 });
        if (!cancelled) setRows(Array.isArray(result) ? result : []);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [q]);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <div className="relative min-w-0 flex-1">
          <Icon name="search" size={16} className="pointer-events-none absolute start-3 top-3 text-slate-500" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث بالاسم أو الرقم القومي أو الهاتف أو كود التأمين"
            className="ps-9"
            aria-label="البحث عن مريض"
          />
        </div>
        {canCreate && (
          <Button icon="user-plus" onClick={onCreateNew}>
            ملف جديد
          </Button>
        )}
      </div>

      {error && <p className="text-sm text-red-300">{error}</p>}

      {loading && !rows ? (
        <Spinner label="جارٍ البحث" variant="inline" />
      ) : rows && rows.length === 0 ? (
        <EmptyState
          icon="search-x"
          title={q ? 'لا يوجد مريض مطابق' : 'لا توجد ملفات بعد'}
          message={canCreate ? 'سجّل ملفاً جديداً للمريض.' : ''}
        />
      ) : (
        <ul className="custom-scrollbar max-h-[55vh] divide-y divide-white/5 overflow-y-auto rounded-xl border border-white/5">
          {(rows || []).map((p) => (
            <li key={p.id}>
              <button
                type="button"
                onClick={() => onSelect(p)}
                className={`flex w-full items-center gap-3 p-3 text-start transition-colors hover:bg-white/5 ${
                  String(selectedId) === String(p.id) ? 'bg-cyan-500/10' : ''
                }`}
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${
                    p.patientType === 'insured' ? 'bg-purple-500/15 text-purple-300' : 'bg-emerald-500/15 text-emerald-300'
                  }`}
                >
                  <Icon name={p.patientType === 'insured' ? 'shield-plus' : 'banknote'} size={18} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-bold text-white">{p.fullName}</span>
                  <span className="block truncate text-xs text-slate-400" dir="ltr">
                    {[p.nationalId, p.phonePrimary, p.insuranceCode].filter(Boolean).join(' · ') || '—'}
                  </span>
                </span>
                <Badge
                  className={
                    p.patientType === 'insured'
                      ? 'border-purple-500/30 bg-purple-500/10 text-purple-200'
                      : 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
                  }
                >
                  {p.patientType === 'insured' ? 'تأمين' : 'نقدي'}
                </Badge>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default PatientFinder;
